import { useTracking } from '@/components/Tracking/useTracking/useTracking'
import { trackInteractionPromise } from '@/modules/core/services/analytics'
import { ImageTKWW } from '@/ui/ImageWrapper/ImageTKWW'
import { Link } from '@/ui/Link'
import { PaperBody1, PaperH2, PaperH3 } from '@/ui/Typography/Typography'
import { FC, Fragment } from 'react'
import { HomePageSection } from '../shared/containers'
import { responsiveWidthFunction } from '../shared/helpers'
import { HomePageSecondarySlide, ValuePropSlide } from '../shared/interface'
import { HomePageData } from './data'
import { CarouselWithPageDots } from './shared/secondary-carousel'
import styles from './styles.module.scss'
import { H1 } from '@/ui/Typography'

interface CarouselTitleInterface {
  title: string
}

const CarouselTitle: FC<CarouselTitleInterface> = ({ title }: CarouselTitleInterface) => {
  return (
    <div className={styles.carouselTitle}>
      <PaperH2> {title} </PaperH2>
    </div>
  )
}

interface SimpleSlideComponentProps extends HomePageSecondarySlide {
  index: number
  hasProducts?: boolean
}

export const SimpleSlide: FC<SimpleSlideComponentProps> = ({ hasProducts, ...slideData }) => {
  const { name, image, url, tracking, index } = slideData
  const trackingData = useTracking()
  const onClickHandler = async () => {
    if (hasProducts && tracking?.clicked) {
      await trackInteractionPromise('Product Clicked', { ...tracking.clicked, position: index, ...trackingData })
    }
  }

  return (
    <div data-testid="simple-slide-primary-cta" onClick={onClickHandler} className={styles.simpleSlideContainer}>
      <ImageTKWW unoptimized className={styles.simpleSlideImage} src={image} />
      <Link className={styles.simpleSlideLinkComponent} href={url} isExternal>
        {' '}
        {name}{' '}
      </Link>
      <PaperBody1 className={styles.simpleSlideText}>{name}</PaperBody1>
    </div>
  )
}

type ValuePropPanelSlideComponentProps = ValuePropSlide

const ValuePropPanelSlide: FC<ValuePropPanelSlideComponentProps> = (props) => {
  const { heading, body, link, image } = props
  return (
    <div className={styles.valuePropSlideContainer}>
      <ImageTKWW unoptimized className={styles.valuePropImage} width={444} height={523} src={image} />
      <div className={styles.valuePropTextOverlay}>
        <div className={styles.valuePropHeading}>
          <PaperH3> {heading} </PaperH3>
        </div>

        <div className={styles.valuePropBody}>
          <PaperBody1>{body}</PaperBody1>
        </div>

        <div className={styles.valuePropLink}>
          <Link color="secondary" href={link.url}>
            {' '}
            {link.title}{' '}
          </Link>
        </div>
      </div>
    </div>
  )
}

interface SecondaryCarouselProps {
  slidesData: HomePageSecondarySlide[]
  title: string
  hasProducts?: boolean
}

const SecondaryCarousel: FC<SecondaryCarouselProps> = ({ slidesData, title, hasProducts = false }) => {
  return (
    <Fragment>
      <CarouselTitle title={title} />
      <CarouselWithPageDots responsive={responsiveWidthFunction} maxSlideWidth={1000} minSlideWidth={290}>
        {slidesData.map((slide, index) => (
          <SimpleSlide key={`${slide.name}`} index={index} {...slide} hasProducts={hasProducts} />
        ))}
      </CarouselWithPageDots>
    </Fragment>
  )
}

const TitleHeaderSection = () => {
  return (
    <HomePageSection>
      <H1 className={styles.titleHeader}>
        Wedding Invitations, Day of Stationery and Save the Dates, All Designed for You
      </H1>
    </HomePageSection>
  )
}

const ValuePropPanelCarouselContainer: FC = () => {
  const { valuePropData } = HomePageData

  return (
    <HomePageSection>
      <CarouselWithPageDots maxSlideWidth={1000} minSlideWidth={290} responsive={responsiveWidthFunction}>
        {valuePropData.map((slide) => (
          <ValuePropPanelSlide key={slide.heading} {...slide} />
        ))}
      </CarouselWithPageDots>
    </HomePageSection>
  )
}

const StyleCarouselContainer: FC = () => {
  const { styles } = HomePageData
  return (
    <HomePageSection>
      <SecondaryCarousel slidesData={styles} title="Shop designs by style" />
    </HomePageSection>
  )
}

const CategoryCarouselContainer: FC = () => {
  const { categories } = HomePageData
  return (
    <HomePageSection>
      <SecondaryCarousel slidesData={categories} title="Shop by category" />
    </HomePageSection>
  )
}

const TrendingDesignCarouselContainer: FC = () => {
  const { trendingDesigns } = HomePageData
  return (
    <HomePageSection>
      <SecondaryCarousel slidesData={trendingDesigns} title="Check out these trending designs" hasProducts />
    </HomePageSection>
  )
}

const BottomSection: FC = () => {
  return (
    <Fragment>
      <TitleHeaderSection />
      <ValuePropPanelCarouselContainer />
      <StyleCarouselContainer />
      <CategoryCarouselContainer />
      <TrendingDesignCarouselContainer />
    </Fragment>
  )
}

export default BottomSection
